import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PublicURL } from "../../routes/public_routes/PublicURL";
import "../../css/pages/other_pages/NotFoundPage.css";

const NotFoundPage = () => {
    const canvasRef = useRef(null);
    const cardRef = useRef(null);
    const glowRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        let width = 0;
        let height = 0;

        const resize = () => {
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
        };

        resize();
        window.addEventListener("resize", resize);

        const PARTICLE_COUNT = 120;
        const MAX_CONNECTION = 150;
        const MIN_TRIANGLE_DISTANCE = 100;

        let particles = [];

        const createParticles = () => {
            particles = [];
            for (let i = 0; i < PARTICLE_COUNT; i++) {
                particles.push({
                x: Math.random() * width,
                y: Math.random() * height,
                vx: (Math.random() - 0.5) * 2,
                vy: (Math.random() - 0.5) * 2,
                radius: 2,
                });
            }
        };

        createParticles();

        let animationFrame;

        const render = () => {
            ctx.clearRect(0, 0, width, height);

            // MOVE PARTICLES
            particles.forEach((p) => {
                p.x += p.vx;
                p.y += p.vy;

                if (p.x < 0 || p.x > width) p.vx *= -1;
                if (p.y < 0 || p.y > height) p.vy *= -1;

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                ctx.fillStyle = "cyan";
                ctx.fill();
            });

            // CONNECTIONS
            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const p1 = particles[i];
                    const p2 = particles[j];

                    const dx = p1.x - p2.x;
                    const dy = p1.y - p2.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    if (dist < MAX_CONNECTION) {
                        ctx.beginPath();
                        ctx.moveTo(p1.x, p1.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.strokeStyle = `rgba(0,241,255,${1 - dist / MAX_CONNECTION})`;
                        ctx.lineWidth = 0.5;
                        ctx.stroke();
                    }

                    // TRIANGLES
                    for (let k = j + 1; k < particles.length; k++) {
                        const p3 = particles[k];

                        const dx2 = p1.x - p3.x;
                        const dy2 = p1.y - p3.y;
                        const dx3 = p2.x - p3.x;
                        const dy3 = p2.y - p3.y;

                        const dist2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
                        const dist3 = Math.sqrt(dx3 * dx3 + dy3 * dy3);

                        if (
                            dist < MIN_TRIANGLE_DISTANCE &&
                            dist2 < MIN_TRIANGLE_DISTANCE &&
                            dist3 < MIN_TRIANGLE_DISTANCE
                        ) {
                            ctx.beginPath();
                            ctx.moveTo(p1.x, p1.y);
                            ctx.lineTo(p2.x, p2.y);
                            ctx.lineTo(p3.x, p3.y);
                            ctx.closePath();

                            const alpha =
                                0.05 *
                                (3 -
                                (dist + dist2 + dist3) /
                                    (MIN_TRIANGLE_DISTANCE * 3));

                            ctx.fillStyle = `rgba(0,255,255,${alpha})`;
                            ctx.fill();
                        }
                    }
                }
            }

            animationFrame = requestAnimationFrame(render);
        };

        render();

        return () => {
            cancelAnimationFrame(animationFrame);
            window.removeEventListener("resize", resize);
        };
    }, []);

    // CURSOR GLOW EFFECT
    useEffect(() => {
        const card = cardRef.current;
        const glow = glowRef.current;

        if (!card || !glow) return;

        const moveGlow = (e) => {
            const rect = card.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            glow.style.left = `${x}px`;
            glow.style.top = `${y}px`;
        };

        card.addEventListener("mousemove", moveGlow);

        return () => { card.removeEventListener("mousemove", moveGlow); };
    }, []);

    return (
        <div className="error-animation-container">
            <canvas ref={canvasRef} className="error-canvas" />

            <div className="error-content">
                <div className="error-card" ref={cardRef}>
                    <h1>404</h1>
                    <p>Page Not Found</p>

                    <button onClick={() => navigate(PublicURL.HOME)}>
                        Go to Home
                    </button>

                    <div className="cursor-glow" ref={glowRef}></div>
                </div>
            </div>
        </div>
    );
};

export default NotFoundPage;
